/**
 * Global error handling configuration for React Query.
 *
 * To suppress global error notifications for a specific query/mutation,
 * use: meta: { suppressGlobalError: true }
 */

export type GlobalErrorHandlingMeta = {
  /** Primary flag - use this one to suppress global error notifications */
  suppressGlobalError?: boolean;
  /** @deprecated Use suppressGlobalError instead */
  suppressGlobalErrorSnackbar?: boolean;
  /** @deprecated Use suppressGlobalError instead */
  disableGlobalErrorSnackbar?: boolean;
  /** @deprecated Use suppressGlobalError instead */
  handledError?: boolean;
};

/** Helper to format queryKey for logging (handles objects in queryKey) */
export const formatQueryKey = (queryKey: readonly unknown[]): string => {
  return queryKey
    .map((part) =>
      typeof part === "object" && part !== null ? JSON.stringify(part) : String(part)
    )
    .join("/");
};

/** Extract meta from a query or mutation object */
export const extractMeta = (queryOrMutation: unknown): unknown => {
  const obj = queryOrMutation as Record<string, unknown> | undefined;
  return obj?.options?.meta ?? obj?.meta;
};

/** Check if global error snackbar should be suppressed based on meta flags */
export const shouldSuppressGlobalErrorSnackbar = (meta: unknown): boolean => {
  if (!meta || typeof meta !== "object") return false;
  const m = meta as Record<string, unknown>;
  return Boolean(
    m["suppressGlobalError"] ||
      m["suppressGlobalErrorSnackbar"] ||
      m["disableGlobalErrorSnackbar"] ||
      m["handledError"]
  );
};

/** Get error message from an unknown error */
export const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  if (error && typeof error === "object") {
    const obj = error as Record<string, unknown>;
    // Handle objects with a message property (e.g., plain error objects)
    if (typeof obj.message === "string") return obj.message;
    // Handle axios-style errors with response.data.message
    if (obj.response && typeof obj.response === "object") {
      const resp = obj.response as Record<string, unknown>;
      if (resp.data && typeof resp.data === "object") {
        const data = resp.data as Record<string, unknown>;
        if (typeof data.message === "string") return data.message;
      }
    }
  }
  return "Unknown error";
};
